import { useEffect, useMemo, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { LocationApiKeyValidator } from '@/app/session/location-api-key-validator';
import {
  LOCATION_CREDENTIAL_SOURCES,
  maskLocationApiKey,
  readStartupSession,
  removeLocationCredential,
  type StartupSessionSnapshot,
  saveUserEmail,
  saveValidatedLocationApiKey,
  selectLocationCredential,
  stripLocationApiKeySearchParams,
} from '@/app/session/startup-session';
import { messages } from './messages';

export const STARTUP_STEPS = {
  email: 'email',
  location: 'location',
  ready: 'ready',
} as const;
export type StartupStep = (typeof STARTUP_STEPS)[keyof typeof STARTUP_STEPS];

export function useStartupGateState() {
  const intl = useIntl();
  const validator = useMemo(() => new LocationApiKeyValidator(), []);
  const [snapshot, setSnapshot] = useState<StartupSessionSnapshot>(() => readStartupSession());
  const [step, setStep] = useState<StartupStep>(() => readInitialStartupStep(snapshot));
  const [email, setEmail] = useState(snapshot.email);
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [locationApiKeyError, setLocationApiKeyError] = useState<string | null>(null);
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [isValidatingLocation, setIsValidatingLocation] = useState(false);

  useEffect(() => {
    if (!snapshot.parsedLocationApiKey) {
      return;
    }

    let isCancelled = false;

    async function validateParsedLocation() {
      setError(null);
      setIsValidatingLocation(true);

      try {
        const validation = await validator.validate(snapshot.parsedLocationApiKey);

        if (isCancelled) {
          return;
        }

        const saved = saveValidatedLocationApiKey({
          apiKey: snapshot.parsedLocationApiKey,
          locationId: validation.id,
          locationName: readLocationDisplayName(intl, validation, snapshot.parsedLocationApiKey),
          source: LOCATION_CREDENTIAL_SOURCES.url,
        });
        stripLocationApiKeySearchParams();
        setSnapshot(current => ({
          ...current,
          locations: saved.locations,
          parsedLocationApiKey: '',
          parsedLocationId: null,
          selectedLocationId: saved.selectedLocationId,
        }));
      } catch (error) {
        if (!isCancelled) {
          setError(
            error instanceof Error ? error.message : intl.formatMessage(messages.startupErrorValidateLocationApiKey),
          );
        }
      } finally {
        if (!isCancelled) {
          setIsValidatingLocation(false);
        }
      }
    }

    void validateParsedLocation();

    return () => {
      isCancelled = true;
    };
  }, [intl, snapshot.parsedLocationApiKey, validator]);

  function handleEmailSubmit() {
    setError(null);

    try {
      const saved = saveUserEmail(email);
      setSnapshot(current => ({ ...current, email: saved.email }));
      setEmail(saved.email);
      setStep(STARTUP_STEPS.location);
    } catch (error) {
      setError(error instanceof Error ? error.message : intl.formatMessage(messages.startupErrorSaveEmail));
    }
  }

  async function handleLocationSubmit() {
    setError(null);
    setLocationApiKeyError(null);
    setIsValidatingLocation(true);

    try {
      const validation = await validator.validate(apiKey);
      const saved = saveValidatedLocationApiKey({
        apiKey,
        locationId: validation.id,
        locationName: readLocationDisplayName(intl, validation, apiKey),
        source: LOCATION_CREDENTIAL_SOURCES.manual,
      });
      setSnapshot(current => ({
        ...current,
        locations: saved.locations,
        parsedLocationApiKey: '',
        parsedLocationId: null,
        selectedLocationId: saved.selectedLocationId,
      }));
      setApiKey('');
      setIsAddLocationOpen(false);
    } catch {
      setLocationApiKeyError(intl.formatMessage(messages.startupInvalidLocationApiKey));
    } finally {
      setIsValidatingLocation(false);
    }
  }

  function handleApiKeyChange(value: string) {
    setApiKey(value);
    setLocationApiKeyError(null);
  }

  function handleAddLocationOpen() {
    setError(null);
    setLocationApiKeyError(null);
    setIsAddLocationOpen(true);
  }

  function handleAddLocationCancel() {
    setLocationApiKeyError(null);
    setIsAddLocationOpen(false);
  }

  function handleSelectLocation(locationId: string) {
    setError(null);

    try {
      selectLocationCredential(locationId);
      setSnapshot(current => ({ ...current, selectedLocationId: locationId }));
    } catch (error) {
      setError(error instanceof Error ? error.message : intl.formatMessage(messages.startupErrorSelectLocation));
    }
  }

  function handleRemoveLocation(locationId: string) {
    setError(null);

    try {
      const saved = removeLocationCredential(locationId);
      setSnapshot(current => ({
        ...current,
        locations: saved.locations,
        selectedLocationId: saved.selectedLocationId,
      }));
    } catch (error) {
      setError(error instanceof Error ? error.message : intl.formatMessage(messages.startupErrorSelectLocation));
    }
  }

  function handleContinue() {
    if (!snapshot.selectedLocationId) {
      setError(intl.formatMessage(messages.startupChooseLocationBeforeContinuing));
      return;
    }

    setSnapshot(current => ({ ...current, parsedLocationId: null }));
    setStep(STARTUP_STEPS.ready);
  }

  return {
    activeLocation: snapshot.locations.find(location => location.id === snapshot.selectedLocationId) ?? null,
    apiKey,
    email,
    error,
    isAddLocationOpen,
    isValidatingLocation,
    locationApiKeyError,
    setApiKey: handleApiKeyChange,
    setEmail,
    openAddLocation: handleAddLocationOpen,
    cancelAddLocation: handleAddLocationCancel,
    setStep,
    snapshot,
    step,
    submitEmail: handleEmailSubmit,
    submitLocation: handleLocationSubmit,
    selectLocation: handleSelectLocation,
    removeLocation: handleRemoveLocation,
    continueToApp: handleContinue,
  };
}

function readInitialStartupStep(snapshot: StartupSessionSnapshot): StartupStep {
  if (snapshot.email && snapshot.selectedLocationId && !snapshot.parsedLocationId) {
    return STARTUP_STEPS.ready;
  }

  return snapshot.email ? STARTUP_STEPS.location : STARTUP_STEPS.email;
}

function readLocationDisplayName(intl: IntlShape, location: { name: string | null }, apiKey: string) {
  return (
    location.name?.trim() ||
    intl.formatMessage(messages.startupLocationFallbackName, {
      apiKey: maskLocationApiKey(apiKey),
    })
  );
}
